import type { McpProbeResult, McpTrustTier } from '@ralph/contracts'
import clsx from 'clsx'
import { AlertTriangle, ArrowLeft, KeyRound, Terminal } from 'lucide-react'
import { useId, useState } from 'react'
import { ApiRequestError } from '@/api/client.ts'
import type { AuthorizeMode } from '@/api/systems.ts'
import { Notice, SectionHead } from '@/components/ui/admin-chrome.tsx'
import { Badge, Button, Input, Panel, Textarea } from '@/components/ui/primitives.tsx'
import { useAuthorizeAnySystem, useCreateSystem, usePreflight, useProbeAnySystem } from '@/hooks/use-systems.ts'
import { describeApiError, describeIssue, useI18n, useT } from '@/i18n/index.ts'
import { StageLadder } from './StageLadder.tsx'
import { DESCRIPTION_MAX, NAME_MAX } from './system-limits.ts'
import { canContinue, isAnswerable, looksLikeACommand, tidyUrl } from './system-stages.ts'

/**
 * Connecting a system, from one field.
 *
 * The shape of this screen is the argument. Every other console asks for a name, a
 * description, a transport, an authentication kind and a trust level *first*, then tries the
 * connection and says "failed" — which asks somebody to make five decisions about a thing
 * they have not been told anything about, and then punishes them with one word when the
 * fifth was wrong.
 *
 * So: one field, then **the platform shows its working**. Six steps, each with its own
 * verdict, in the order they were attempted. A step that failed says what failed and whose
 * fix it is. A step below the failure says *not reached* rather than failed, because it was
 * never attempted and colouring it red invents a second problem. Only once there is
 * something to say does the form ask for the two things the platform genuinely cannot work
 * out — what to call it, and how far to trust it.
 *
 * The pay-off is not aesthetic. The commonest failure in a deployment behind a firewall is
 * egress policy, and "address fine, could not reach it" sends somebody to the network team
 * on the first try instead of the fourth.
 */

type Step = 'address' | 'details'

export const ConnectFlow = ({ onDone, onCancel }: { onDone: (systemId: string) => void; onCancel: () => void }) => {
  // In the component body, never at module scope: `const t = systemsStrings` at the top of a
  // file freezes the language at import and then renders the previous one after a switch,
  // with nothing failing anywhere (PRD-27 F-3, LOC-012).
  const messages = useT()
  const t = messages.admin.systems
  const fieldId = useId()
  const preflight = usePreflight()
  const create = useCreateSystem()
  const authorize = useAuthorizeAnySystem()
  const probeAfterCreate = useProbeAnySystem()

  const [step, setStep] = useState<Step>('address')
  const [url, setUrl] = useState('')
  const [probed, setProbed] = useState<{ result: McpProbeResult; url: string } | null>(null)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [trustTier, setTrustTier] = useState<McpTrustTier>('untrusted')
  const [tokenRef, setTokenRef] = useState('')
  const [mode, setMode] = useState<AuthorizeMode>('delegated')

  const command = looksLikeACommand(url)
  const result = probed?.result

  const check = async () => {
    const tidied = tidyUrl(url)
    if (tidied === '' || command) return

    const answer = await preflight.mutateAsync({ url: tidied, ...(tokenRef ? { tokenRef } : {}) })
    setProbed({ result: answer.result, url: tidied })
    // The proposed name is a default in a field, never a value written on somebody's behalf:
    // it is derived from what a remote party claims to be called, and adopting that silently
    // is exactly the move the rest of this plane refuses to make.
    if (answer.suggestedName && name === '') setName(answer.suggestedName)
    if (canContinue(answer.result)) setStep('details')
  }

  /**
   * Register, and — where the system said it signs people in — actually sign in.
   *
   * The second half is the part that used to be a sentence promising a step that did not
   * exist. A system that answered `401` was registered as needing no credential at all, so
   * it could never work, and the flow said "the next step opens its sign-in page" on the way
   * to a dead end. The step is here now: a real top-level navigation to the authority, which
   * comes back to `/agents/studio` with the outcome.
   */
  const submit = async () => {
    if (!probed || !result) return
    const created = await create.mutateAsync({
      name: name.trim(),
      description: description.trim(),
      url: probed.url,
      authKind: tokenRef ? 'bearer' : 'none',
      tokenRef: tokenRef || null,
      trustTier,
      enabled: true,
      access: { kind: 'everyone', groupIds: [] }
    })

    if (!isAnswerable(result.stage)) {
      onDone(created.id)
      return
    }

    // Probed once more so the row records the authority before the sign-in asks for it: the
    // `resource_metadata_url` is learned from the system's own challenge, and starting an
    // authorization without it has nothing to discover from.
    await probeAfterCreate.mutateAsync(created.id)
    const start = await authorize.mutateAsync({
      id: created.id,
      mode,
      ...(result.authorization?.scope ? { challengedScope: result.authorization.scope } : {})
    })
    // A full navigation, not a fetch: the authority renders its own consent page, in its own
    // origin, with its own cookies and its own address bar. Anything less is a phishing
    // lesson taught by the platform.
    window.location.assign(start.authorizeUrl)
  }

  return (
    <section>
      <SectionHead
        title={t.flow.title}
        lead={t.flow.lead}
        action={
          <Button size="sm" onClick={onCancel}>
            {t.flow.cancel}
          </Button>
        }
      />

      {/* A real form, so Enter in the address field checks it — which is what somebody who
          has just pasted a URL will press. The handlers take no event: `FormEvent` is
          deprecated in this React, and the only thing they wanted from it was the default
          suppression, which belongs at the call site anyway. */}
      <form
        className="mt-4 space-y-4"
        onSubmit={(event) => {
          event.preventDefault()
          void (step === 'address' ? check() : submit())
        }}
      >
        <Panel className="px-4 py-3.5">
          <label htmlFor={`${fieldId}-url`} className="eyebrow mb-1 block">
            {t.flow.addressLabel}
          </label>
          <div className="flex flex-wrap items-start gap-2">
            <Input
              id={`${fieldId}-url`}
              value={url}
              type="text"
              inputMode="url"
              autoComplete="off"
              spellCheck={false}
              placeholder={t.flow.addressPlaceholder}
              className="min-w-0 flex-1"
              onChange={(event) => {
                setUrl(event.target.value)
                // A changed address invalidates every verdict below it. Leaving the old
                // ladder on screen beside a new address is the one way this surface could
                // lie: it would be reporting six findings about somewhere else.
                setProbed(null)
                setStep('address')
              }}
            />
            <Button type="submit" variant="primary" disabled={url.trim() === '' || command || preflight.isPending}>
              {preflight.isPending ? t.flow.checking : probed ? t.flow.recheck : t.flow.check}
            </Button>
          </div>
          <p className="mt-1.5 text-[12.5px] leading-snug text-fg-subtle">{t.flow.addressHint}</p>

          {command && (
            <div className="mt-3">
              <Notice title={t.flow.stdioTitle} icon={<Terminal size={13} strokeWidth={2.1} />}>
                {t.flow.stdioBody}
              </Notice>
            </div>
          )}

          {preflight.isError && (
            <p role="alert" className="mt-2.5 text-[12.5px] text-alert">
              {describeApiError(messages, preflight.error)}
            </p>
          )}
        </Panel>

        {(preflight.isPending || result) && (
          <StageLadder result={result} url={probed?.url ?? url} checking={preflight.isPending} showClaim />
        )}

        {result && !result.ok && !isAnswerable(result.stage) && result.hint !== '' && (
          <Notice title={t.probeStage[result.stage ?? 'connect']} icon={<AlertTriangle size={13} strokeWidth={2.1} />}>
            {/* The advice line, from the catalogue. `detail` — the far side's own words, on
                the ladder above — stays verbatim and is the honest passthrough; this is ours
                and is one of sixteen sentences, so it is keyed (PRD-27 D-4). The English
                `hint` remains as the fallback for a key this build has not learned. */}
            {describeIssue(messages, { messageKey: result.hintKey, params: result.hintParams }) ?? result.hint}
          </Notice>
        )}

        {step === 'details' && result && (
          <Details
            fieldId={fieldId}
            result={result}
            name={name}
            description={description}
            trustTier={trustTier}
            tokenRef={tokenRef}
            mode={mode}
            onName={setName}
            onDescription={setDescription}
            onTrust={setTrustTier}
            onToken={setTokenRef}
            onMode={setMode}
            onBack={() => setStep('address')}
            pending={create.isPending || authorize.isPending || probeAfterCreate.isPending}
            error={
              create.isError
                ? create.error instanceof ApiRequestError && create.error.code === 'conflict'
                  ? t.flow.nameTaken
                  : describeApiError(messages, create.error)
                : authorize.isError
                  ? describeApiError(messages, authorize.error)
                  : null
            }
          />
        )}
      </form>
    </section>
  )
}

const TRUST_TIERS: McpTrustTier[] = ['untrusted', 'reviewed', 'first_party']

/**
 * The two things the platform genuinely cannot work out, and the credential if one is wanted.
 *
 * Asked *after* the diagnosis rather than before it, because both answers depend on what the
 * system turned out to be. Nobody should be inventing a trust level for an address that
 * turns out to be a documentation page.
 */
const Details = ({
  fieldId,
  result,
  name,
  description,
  trustTier,
  tokenRef,
  mode,
  onName,
  onDescription,
  onTrust,
  onToken,
  onMode,
  onBack,
  pending,
  error
}: {
  fieldId: string
  result: McpProbeResult
  name: string
  description: string
  trustTier: McpTrustTier
  tokenRef: string
  mode: AuthorizeMode
  onName: (value: string) => void
  onDescription: (value: string) => void
  onTrust: (value: McpTrustTier) => void
  onToken: (value: string) => void
  onMode: (value: AuthorizeMode) => void
  onBack: () => void
  pending: boolean
  error: string | null
}) => {
  const fmt = useI18n()
  const t = fmt.t.admin.systems
  const wantsSignIn = isAnswerable(result.stage)
  const complete = /^[a-z0-9][a-z0-9-]{1,30}$/.test(name)

  return (
    <>
      <Panel className="space-y-4 px-4 py-3.5">
        <div>
          <label htmlFor={`${fieldId}-name`} className="eyebrow mb-1 block">
            {t.flow.nameLabel}
          </label>
          <Input
            id={`${fieldId}-name`}
            value={name}
            maxLength={NAME_MAX}
            autoComplete="off"
            spellCheck={false}
            onChange={(event) => onName(event.target.value.toLowerCase())}
          />
          <p className="mt-1 text-[12.5px] leading-snug text-fg-subtle">{t.flow.nameHint}</p>
        </div>

        <div>
          <label htmlFor={`${fieldId}-description`} className="eyebrow mb-1 block">
            {t.flow.descriptionLabel}
          </label>
          <Textarea
            id={`${fieldId}-description`}
            rows={2}
            value={description}
            maxLength={DESCRIPTION_MAX}
            placeholder={t.flow.descriptionPlaceholder}
            onChange={(event) => onDescription(event.target.value)}
          />
          <p className="mt-1 text-[12.5px] leading-snug text-fg-subtle">{t.flow.descriptionHint}</p>
        </div>

        <fieldset>
          <legend className="eyebrow mb-1.5">{t.flow.trustLabel}</legend>
          <p className="mb-2 text-[12.5px] leading-snug text-fg-subtle">{t.flow.trustHint}</p>
          <div className="grid gap-1.5">
            {TRUST_TIERS.map((tier) => (
              <label
                key={tier}
                className={clsx(
                  'flex cursor-pointer items-start gap-2 rounded-tile border bg-fg/[0.02] px-3 py-2',
                  'text-[13px] leading-snug transition-colors duration-200',
                  trustTier === tier ? 'border-rule-strong' : 'border-rule hover:border-rule-strong'
                )}
              >
                <input
                  type="radio"
                  name={`${fieldId}-trust`}
                  value={tier}
                  checked={trustTier === tier}
                  onChange={() => onTrust(tier)}
                  className="mt-[3px] h-3.5 w-3.5 shrink-0 accent-primary"
                />
                <span className="min-w-0">
                  <span className="font-semibold">{t.flow.trust[tier].label}</span>
                  <span className="mt-0.5 block text-[12.5px] leading-relaxed text-fg-muted">
                    {t.flow.trust[tier].note}
                  </span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>
      </Panel>

      {wantsSignIn ? (
        <>
          <Notice title={t.flow.credentialOauthTitle} icon={<KeyRound size={13} strokeWidth={2.1} />}>
            {t.flow.credentialOauthBody}
            {result.authorization?.scope && (
              <p className="mt-1.5 text-[12.5px] text-fg-subtle">
                {t.flow.scopeLabel} <span className="readout">{result.authorization.scope}</span>
              </p>
            )}
          </Notice>

          {/* The one genuinely consequential choice here, so it is a choice and not a
              default. One authority model per system (PRD-23 D-6): mixing them makes
              "whose authority was this?" answerable only per call, and makes an approval
              digest that binds identity meaningless. Both options state their consequence
              rather than their mechanism — "the system's own records show who really did
              what" is the thing an administrator is actually deciding between. */}
          <Panel className="px-4 py-3.5">
            <fieldset>
              <legend className="eyebrow mb-1.5">{t.flow.modeLabel}</legend>
              <p className="mb-2 text-[12.5px] leading-snug text-fg-subtle">{t.flow.modeHint}</p>
              <div className="grid gap-1.5">
                {(['delegated', 'service_account'] as const).map((value) => (
                  <label
                    key={value}
                    className={clsx(
                      'flex cursor-pointer items-start gap-2 rounded-tile border bg-fg/[0.02] px-3 py-2',
                      'text-[13px] leading-snug transition-colors duration-200',
                      mode === value ? 'border-rule-strong' : 'border-rule hover:border-rule-strong'
                    )}
                  >
                    <input
                      type="radio"
                      name={`${fieldId}-mode`}
                      value={value}
                      checked={mode === value}
                      onChange={() => onMode(value)}
                      className="mt-[3px] h-3.5 w-3.5 shrink-0 accent-primary"
                    />
                    <span className="min-w-0">
                      <span className="font-semibold">{t.flow.mode[value].label}</span>
                      <span className="mt-0.5 block text-[12.5px] leading-relaxed text-fg-muted">
                        {t.flow.mode[value].note}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>
          </Panel>
        </>
      ) : (
        <Panel className="px-4 py-3.5">
          <label htmlFor={`${fieldId}-token`} className="eyebrow mb-1 block">
            {t.flow.credentialTokenLabel}
          </label>
          <Input
            id={`${fieldId}-token`}
            value={tokenRef}
            autoComplete="off"
            spellCheck={false}
            placeholder={t.flow.credentialTokenPlaceholder}
            onChange={(event) => onToken(event.target.value.trim())}
          />
          <p className="mt-1.5 text-[12.5px] leading-relaxed text-fg-subtle">{t.flow.credentialTokenHint}</p>
        </Panel>
      )}

      {error && (
        <p role="alert" className="text-[12.5px] text-alert">
          {error}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Button type="submit" variant="primary" disabled={!complete || pending}>
          {pending ? (wantsSignIn ? t.flow.signingIn : t.flow.submitting) : wantsSignIn ? t.flow.signIn : t.flow.submit}
        </Button>
        <Button size="sm" onClick={onBack}>
          <ArrowLeft size={13} strokeWidth={2.1} aria-hidden />
          {t.flow.back}
        </Button>
        {/* The same catalogue entry the stage ladder's `offer` row uses. It was written out
            again here, by hand, with its own `=== 1` — two copies of one sentence is one copy
            too many to translate, and both were wrong in Portuguese at zero. */}
        {result.ok && <Badge tone="grounded">{t.toolCount(fmt, result.toolCount)}</Badge>}
      </div>
    </>
  )
}
